/**
 * Authentication Store
 *
 * Central state management for authentication using Zustand.
 * Handles user session, login, logout, and initialization.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CurrentUserResponse, LoginRequest, RegisterRequest } from "@/types/auth";
import { apiClient, ApiRequestError } from "@/lib/api-client";
import { resetUserData } from "./resetUserData";

/** Sets the signed-in user, clearing other stores' data when it belongs to someone else. */
function switchUser(previous: CurrentUserResponse | null, next: CurrentUserResponse | null): void {
  if (previous?.userId !== next?.userId || previous?.currentAccountId !== next?.currentAccountId) {
    resetUserData();
  }
}

interface AuthState {
  // State
  user: CurrentUserResponse | null;
  isLoading: boolean;
  isInitialized: boolean;
  isHydrated: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isLoading: false,
      isInitialized: false,
      isHydrated: true, // Set to true immediately - will be updated if using persisted storage
      error: null,

      // Set hydration status
      setHydrated: () => {
        set({ isHydrated: true });
      },

      // Initialize auth on app load
      initialize: async () => {
        const state = get();

        if (state.isInitialized) {
          return; // Already initialized
        }

        set({ isLoading: true });

        try {
          // Call /auth/me to check if user is authenticated
          const user = await apiClient.get<CurrentUserResponse>("/auth/me");
          switchUser(get().user, user);
          set({ user, isInitialized: true, isLoading: false, error: null });
        } catch (error) {
          // Always set isInitialized and clear isLoading, regardless of error type

          // Handle 401 (not authenticated) gracefully
          if (error instanceof ApiRequestError && error.status === 401) {
            switchUser(get().user, null);
            set({
              user: null,
              isInitialized: true,
              isLoading: false,
              error: null,
            });
          } else {
            // Network error or other error - set initialized anyway to allow app to load
            const errorMessage = error instanceof ApiRequestError 
              ? error.message 
              : "Unable to connect to server. Working in offline mode.";
            console.warn("Working in offline mode:", errorMessage);
            set({ 
              user: null, 
              isInitialized: true, 
              isLoading: false,
              error: null // Don't show error to user for network issues on init
            });
          }
        }
      },

      // Login user
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.post("/auth/login", credentials);

          // After login, fetch full user data from /auth/me
          const user = await apiClient.get<CurrentUserResponse>("/auth/me");
          switchUser(get().user, user);
          set({ user, isLoading: false, error: null });
        } catch (error) {
          const errorMessage =
            error instanceof ApiRequestError
              ? error.message
              : "Invalid email or password";
          set({
            error: errorMessage,
            isLoading: false,
            user: null,
          });
          throw error;
        }
      },

      // Register new user
      register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.post("/auth/register", data);
          const user = await apiClient.get<CurrentUserResponse>("/auth/me");
          switchUser(get().user, user);
          set({ user, isLoading: false, error: null });
        } catch (error) {
          const errorMessage =
            error instanceof ApiRequestError
              ? error.message
              : "Registration failed. Please try again.";
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      // Logout user
      logout: async () => {
        set({ isLoading: true });
        try {
          await apiClient.post("/auth/logout");
        } catch (error) {
          console.error("Logout error:", error);
        } finally {
          resetUserData();
          set({ user: null, error: null, isLoading: false });
        }
      },

      // Refresh user data from /auth/me
      refreshUser: async () => {
        try {
          const user = await apiClient.get<CurrentUserResponse>("/auth/me");
          set({ user, error: null });
        } catch (error) {
          if (error instanceof ApiRequestError && error.status === 401) {
            switchUser(get().user, null);
            set({ user: null });
          } else {
            console.error("Failed to refresh user:", error);
          }
        }
      },

      // Clear error message
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: "auth-storage",
      // Only persist user data, not loading/error states
      partialize: (state) => ({ user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated();
        }
      },
    }
  )
);
